import { Chip, cn, ErrorBanner } from '@patchbay/ui'
import type * as React from 'react'
import type { IngestHealth } from '../../lib/api/generated'
import { formatWhen } from '../../lib/display-time'

/**
 * Money, percentages and hours for the profitability screen. A money field the serializer stripped for this role is
 * ABSENT from the payload (hard rule 5): it renders "—", never $0.00. Amounts are CAD pre-tax decimal strings.
 */
export const DASH = '—'

const CAD = new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' })
const CAD0 = new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 })

export type MoneyValue = string | number | null | undefined

export function toNumber(value: MoneyValue): number | null {
  if (value === null || value === undefined || value === '') return null
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : null
}

/** `blankZero`: an ingest is unreachable, so a zero is not evidence (P11): show "—" instead. */
export function money(value: MoneyValue, opts: { whole?: boolean; blankZero?: boolean } = {}): string {
  const n = toNumber(value)
  if (n === null) return DASH
  if (opts.blankZero && n === 0) return DASH
  return (opts.whole ? CAD0 : CAD).format(n)
}

export function pct(value: MoneyValue): string {
  const n = toNumber(value)
  return n === null ? DASH : `${n.toFixed(1)}%`
}

export function hours(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined) return DASH
  return `${(minutes / 60).toFixed(1)}h`
}

export function ratio(value: MoneyValue): string {
  const n = toNumber(value)
  return n === null ? DASH : n.toFixed(2)
}

/** Sort key for a possibly-absent money value: absent sorts below every real figure. */
export function sortMoney(value: MoneyValue): number {
  return toNumber(value) ?? Number.NEGATIVE_INFINITY
}

export function monthLabel(value: string): string {
  const [y, m] = value.slice(0, 7).split('-').map(Number) as [number, number]
  if (!y || !m) return value
  const month = formatWhen(new Date(y, m - 1, 1, 12).toISOString(), { style: 'date' }).split(' ')[0]
  return `${month} ${y}`
}

export function dateLabel(value: string | null | undefined): string {
  if (!value) return DASH
  const d = new Date(`${value.slice(0, 10)}T12:00:00`)
  return Number.isNaN(d.valueOf()) ? value : formatWhen(d.toISOString(), { style: 'date' })
}

export function timeLabel(value: string | null | undefined): string {
  if (!value) return 'never'
  const d = new Date(value)
  return Number.isNaN(d.valueOf()) ? value : formatWhen(d.toISOString(), { style: 'absolute' })
}

export const BOOKS_ORG_LABEL: Record<string, string> = { '847122427': 'Ewee', '914269936': 'SmartPBX', ewee: 'Ewee', smartpbx: 'SmartPBX' }

/** A figure built on a placeholder rate or a non-exact match is shown, and marked. */
export function NotQuotable({ reason }: { reason?: string }) {
  const why = reason ? `not quotable: ${reason}` : 'not quotable: placeholder rate / name match'
  return (
    <Chip tone="warning" size="sm" title={why} aria-label={why}>
      not quotable
    </Chip>
  )
}

/** Money cell: the amount, plus the not-quotable mark when the figure may not be quoted. */
export function Money({
  value,
  quotable = true,
  reason,
  blankZero,
  className,
}: {
  value: MoneyValue
  quotable?: boolean
  reason?: string
  blankZero?: boolean
  className?: string
}) {
  const n = toNumber(value)
  return (
    <span className={cn('tabular inline-flex items-center gap-1', n !== null && n < 0 && 'text-destructive', className)}>
      {money(value, { blankZero })}
      {!quotable && n !== null ? <NotQuotable reason={reason} /> : null}
    </span>
  )
}

export function ingestLabel(h: IngestHealth): string {
  const org = h.org_id ? ` (${BOOKS_ORG_LABEL[h.org_id] ?? h.org_id})` : ''
  return `${h.kind.replaceAll('_', ' ')}${org}`
}

/** Ingests that have not succeeded: the house "Unreachable, last good run <time>" banner. Never zeros. */
export function failedIngests(list: IngestHealth[] | undefined): IngestHealth[] {
  return (list ?? []).filter((h) => h.state === 'failed' || h.state === 'never')
}

export function UnreachableBanner({ items }: { items: IngestHealth[] }) {
  if (items.length === 0) return null
  return (
    <ErrorBanner
      tone="warning"
      title="Unreachable"
      detail={
        <span className="block space-y-0.5">
          {items.map((h) => (
            <span key={`${h.kind}-${h.org_id ?? ''}`} className="block">
              {ingestLabel(h)}: unreachable, last good run {h.last_good_at ? timeLabel(h.last_good_at) : 'never'}. Figures that depend on it
              are not zero; they are unknown.
            </span>
          ))}
        </span>
      }
    />
  )
}

export function Tile({
  label,
  value,
  sub,
  tone,
  children,
}: {
  label: string
  value: React.ReactNode
  sub?: React.ReactNode
  tone?: 'warning' | 'danger'
  children?: React.ReactNode
}) {
  return (
    <div
      className={cn(
        'min-w-0 rounded-lg border border-border bg-card px-3.5 py-3 shadow-xs',
        tone === 'warning' && 'border-warning/40',
        tone === 'danger' && 'border-destructive/40',
      )}
    >
      <p className="truncate text-xs font-medium text-muted-foreground">{label}</p>
      <p className="tabular mt-0.5 truncate text-xl font-semibold">{value}</p>
      {sub ? <p className="mt-0.5 truncate text-xs text-muted-foreground">{sub}</p> : null}
      {children}
    </div>
  )
}
